import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useAuth } from '@/context/AuthContext'
import { useServices } from '@/context/ServiceContext'
import { useSarahUI } from '@/context/SarahUIContext'
import { buildSarahContext } from '@/services/ai/sarahContext'
import { domainEvents } from '@/services/events/domainEvents'
import { SarahAvatar } from './SarahAvatar'
import type { SarahContext, SarahMessage, PracticeScenario } from '@/domain/sarah'
import type { EpisodePerformanceEntry } from '@/domain/analytics'

const STARTER_PROMPTS = [
  'Explain this topic',
  'Help me understand my mistake',
  'Give me a phishing scenario',
  'Test me',
  'What should I review next?',
]

function contextLabel(context: SarahContext | null): string {
  if (!context) return ''
  if (context.episodeTitle) return `Talking about: ${context.episodeTitle}`
  return 'General cybersecurity coaching'
}

// The Sarah AI Coach conversation panel (Document 11 §P3). Deliberately not
// a generic chatbot layout — CyberTales branding (avatar, panel chrome,
// pills), a visible context indicator, scenario cards, and suggested-prompt
// chips instead of a bare text box. Talks only to the AIService/
// SarahSessionRepository interfaces via useServices() — never touches
// localStorage or an AI provider directly, so swapping DevAIService for
// CloudSecureAIService in P5 requires no change here.
export function SarahPanel() {
  const { isOpen, options, closeSarah } = useSarahUI()
  const { currentUser } = useAuth()
  const { episodes, results, progress, xp, badges, orgAnalytics, ai, sarahSessions } = useServices()

  const [context, setContext] = useState<SarahContext | null>(null)
  const [messages, setMessages] = useState<SarahMessage[]>([])
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [answeredScenarioMessageIds, setAnsweredScenarioMessageIds] = useState<Set<string>>(new Set())
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen || !currentUser) return
    let cancelled = false
    setLoading(true)

    async function load() {
      const [episode, quizResult, progressList, allEpisodes, cyberPoints, badgeAwards, session] = await Promise.all([
        options.episodeId ? episodes.getById(options.episodeId) : Promise.resolve(null),
        options.resultId ? results.getById(options.resultId) : Promise.resolve(null),
        progress.getForUser(currentUser!.id),
        episodes.list(),
        xp.getTotalForUser(currentUser!.id),
        badges.listAwardsForUser(currentUser!.id),
        sarahSessions.getOrCreateSession(currentUser!.id, currentUser!.orgId),
      ])

      let weakestOrgEpisode: EpisodePerformanceEntry | null = null
      if (currentUser!.orgId) {
        const perf = await orgAnalytics.getEpisodePerformance(currentUser!.orgId)
        const withRates = perf.filter((p) => p.passRatePercent !== null)
        weakestOrgEpisode =
          withRates.length > 0
            ? withRates.reduce((min, p) => ((p.passRatePercent ?? 100) < (min.passRatePercent ?? 100) ? p : min))
            : null
      }

      if (cancelled) return

      const builtContext = buildSarahContext({
        user: currentUser!,
        episode,
        quizResult,
        weakestOrgEpisode,
        completedEpisodeCount: progressList.filter((p) => p.completed).length,
        totalEpisodeCount: allEpisodes.length,
        cyberPoints,
        badgesEarned: badgeAwards.length,
      })

      setContext(builtContext)
      setMessages(session.messages)
      setLoading(false)

      if (options.initialPrompt) {
        await sendPrompt(options.initialPrompt, builtContext, session.messages)
      }
    }
    load()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, options])

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages])

  async function sendPrompt(promptText: string, ctx: SarahContext, historyBefore: SarahMessage[]) {
    if (!currentUser || !promptText.trim()) return
    const userMsg: SarahMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      text: promptText,
      createdAt: new Date().toISOString(),
    }
    setMessages((m) => [...m, userMsg])
    setSending(true)
    await sarahSessions.appendMessage(currentUser.id, userMsg)

    const reply = await ai.ask({ prompt: promptText, context: ctx, history: historyBefore })
    const sarahMsg: SarahMessage = {
      id: crypto.randomUUID(),
      role: 'sarah',
      text: reply.message,
      createdAt: new Date().toISOString(),
      scenario: reply.scenario,
      followUpPrompts: reply.followUpPrompts,
    }
    setMessages((m) => [...m, sarahMsg])
    await sarahSessions.appendMessage(currentUser.id, sarahMsg)
    setSending(false)
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!context || sending || !inputValue.trim()) return
    const text = inputValue
    setInputValue('')
    await sendPrompt(text, context, messages)
  }

  async function handleFollowUp(prompt: string) {
    if (!context || sending) return
    await sendPrompt(prompt, context, messages)
  }

  async function handleScenarioAnswer(message: SarahMessage, scenario: PracticeScenario, optionId: string) {
    if (!currentUser || !context || sending) return
    setAnsweredScenarioMessageIds((s) => new Set(s).add(message.id))

    const optionText = scenario.options.find((o) => o.id === optionId)?.text ?? optionId
    const userMsg: SarahMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      text: optionText,
      createdAt: new Date().toISOString(),
    }
    setMessages((m) => [...m, userMsg])
    setSending(true)
    await sarahSessions.appendMessage(currentUser.id, userMsg)

    const reply = await ai.answerScenario(scenario, optionId, context)
    const sarahMsg: SarahMessage = {
      id: crypto.randomUUID(),
      role: 'sarah',
      text: reply.message,
      createdAt: new Date().toISOString(),
      followUpPrompts: reply.followUpPrompts,
    }
    setMessages((m) => [...m, sarahMsg])
    await sarahSessions.appendMessage(currentUser.id, sarahMsg)

    // P4: practice scenarios feed the same gamification/streak pipeline as
    // every other learning event — GamificationService/StreakService react
    // to this, Sarah's panel doesn't award anything itself.
    await domainEvents.emit('scenario.completed', {
      userId: currentUser.id,
      orgId: currentUser.orgId,
      scenarioId: scenario.id,
      topic: scenario.topic,
      correct: optionId === scenario.correctOptionId,
    })
    setSending(false)
  }

  async function handleClear() {
    if (!currentUser) return
    await sarahSessions.clearSession(currentUser.id)
    setMessages([])
    setAnsweredScenarioMessageIds(new Set())
  }

  if (!isOpen) return null

  const lastMessage = messages[messages.length - 1]

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button
        aria-label="Close Sarah"
        onClick={closeSarah}
        className="absolute inset-0 bg-navy-950/70 backdrop-blur-sm"
      />
      <div className="relative flex h-full w-full max-w-md flex-col border-l border-white/10 bg-navy-900 shadow-2xl">
        <div className="flex items-center gap-3 border-b border-white/10 p-4">
          <SarahAvatar size={40} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="font-display font-semibold text-white">Sarah</p>
              <span className="cyber-pill bg-amber-500/15 text-amber-300 ring-1 ring-amber-500/30 !px-2 !py-0.5 text-[10px]">
                AI Coach — Preview
              </span>
            </div>
            <p className="truncate text-xs text-white/50">{contextLabel(context)}</p>
          </div>
          <button onClick={handleClear} className="text-xs text-white/40 hover:text-white/70" title="Clear conversation">
            Clear
          </button>
          <button onClick={closeSarah} className="text-xl leading-none text-white/50 hover:text-white" aria-label="Close">
            ×
          </button>
        </div>

        <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto p-4">
          {loading ? (
            <p className="text-sm text-white/50">Loading your coaching context…</p>
          ) : messages.length === 0 ? (
            <div>
              <div className="flex items-start gap-2">
                <SarahAvatar size={28} />
                <div className="cyber-panel max-w-[85%] p-3 text-sm text-white/85">
                  Hi {context?.learnerFirstName ?? 'there'}! I'm Sarah, your CyberTales Learning coach. I can explain
                  a concept, walk through a mistake, or run you through a quick scenario. What would help?
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {STARTER_PROMPTS.map((p) => (
                  <button
                    key={p}
                    onClick={() => handleFollowUp(p)}
                    className="cyber-pill bg-white/5 text-white/70 ring-1 ring-white/10 hover:bg-white/10"
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((m) => (
              <div key={m.id}>
                <div className={`flex items-start gap-2 ${m.role === 'user' ? 'flex-row-reverse' : ''}`}>
                  {m.role === 'sarah' && <SarahAvatar size={28} />}
                  <div
                    className={`max-w-[85%] whitespace-pre-line rounded-2xl p-3 text-sm ${
                      m.role === 'user' ? 'bg-teal-500/20 text-white' : 'cyber-panel text-white/85'
                    }`}
                  >
                    {m.text}
                  </div>
                </div>

                {m.scenario && (
                  <div className="ml-9 mt-2 space-y-2">
                    {m.scenario.options.map((opt) => {
                      const answered = answeredScenarioMessageIds.has(m.id)
                      return (
                        <button
                          key={opt.id}
                          disabled={answered || sending}
                          onClick={() => handleScenarioAnswer(m, m.scenario!, opt.id)}
                          className="block w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-left text-xs text-white/80 transition hover:border-teal-400/40 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {opt.text}
                        </button>
                      )
                    })}
                  </div>
                )}

                {m.role === 'sarah' && m.id === lastMessage?.id && m.followUpPrompts && m.followUpPrompts.length > 0 && (
                  <div className="ml-9 mt-2 flex flex-wrap gap-2">
                    {m.followUpPrompts.map((p) => (
                      <button
                        key={p}
                        onClick={() => handleFollowUp(p)}
                        disabled={sending}
                        className="cyber-pill bg-white/5 text-white/70 ring-1 ring-white/10 hover:bg-white/10 disabled:opacity-50"
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
          {sending && <p className="ml-9 text-xs text-white/40">Sarah is typing…</p>}
        </div>

        <form onSubmit={handleSubmit} className="flex items-center gap-2 border-t border-white/10 p-3">
          <input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Ask Sarah anything about cyber safety…"
            disabled={loading || sending}
            className="flex-1 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-white placeholder:text-white/30 focus:border-teal-400/50 focus:outline-none"
          />
          <button
            type="submit"
            disabled={loading || sending || !inputValue.trim()}
            className="cyber-btn-primary !px-4 !py-2 text-sm disabled:cursor-not-allowed disabled:opacity-40"
          >
            Send
          </button>
        </form>
      </div>
    </div>
  )
}
